import type { BackendTeam, TeamDomain, RosterPlayer } from '@/types';
import { buildPlayerDisplayName, splitLegacyName } from '@/utils/playerName';

export function mapBackendTeam(team: BackendTeam): TeamDomain {
  const roster: RosterPlayer[] = (team.players ?? []).map((player) => {
    const hasSplit = Boolean((player.firstName ?? '').trim() || (player.lastName ?? '').trim());
    const split = hasSplit
      ? { firstName: player.firstName ?? '', lastName: player.lastName ?? '' }
      : splitLegacyName(player.name ?? '');
    return {
      id: String(player.id),
      name: buildPlayerDisplayName({ firstName: split.firstName, lastName: split.lastName, name: player.name ?? '' }),
      firstName: split.firstName,
      lastName: split.lastName,
      role: player.role ?? 'R4',
      // The API sends `null` for a player without a number; screens test `undefined` ("#null").
      number: player.number ?? undefined,
    };
  });

  return {
    id: String(team.id),
    name: team.name,
    level: team.level,
    gender: team.gender,
    season: team.season ?? null,
    roster,
    teamTactics: team.teamTactics,
    // Sans ces deux lignes, les couleurs revenaient vides à chaque relecture :
    // l'enregistrement passait, mais l'écran n'en voyait jamais le résultat.
    jerseyColor: team.jerseyColor ?? null,
    jerseyColorAway: team.jerseyColorAway ?? null,
    logoUrl: team.logoUrl ?? null,
    locked: team.locked ?? false,
    pastSeasonReadOnly: team.pastSeasonReadOnly ?? false,
    viewerOwnsPlan: team.viewerOwnsPlan ?? false,
    // Absent of an older API response: decided by name, and the bin stays.
    ...(team.isDemo !== undefined ? { isDemo: team.isDemo } : {}),
    viewerCanDelete: team.viewerCanDelete ?? true,
    // Absent of an older API response = the default: everything visible.
    playersSeeOwnMatches: team.playersSeeOwnMatches ?? true,
    playersSeeScoutingMatches: team.playersSeeScoutingMatches ?? true,
    playerStatsAccess: team.playerStatsAccess ?? true,
    playerVideoAccess: team.playerVideoAccess ?? true,
    playerBadgeSettings: team.playerBadgeSettings ?? null,
    carriedOverFromId: team.carriedOverFromId ?? null,
  };
}

export function mapBackendTeams(teams: BackendTeam[]): TeamDomain[] {
  return teams.map(mapBackendTeam);
}
